import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileCheck,
  FileText,
  ClipboardCheck,
  Star,
  MapPin,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Award,
  Plane,
  Building,
  Route,
  Sun,
  Trees,
  Factory,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import TypingText from "../components/TypingText"; // adjust the path if needed
import BookingModal from "../components/BookingModal";
import {
  projects,
  testimonials,
  stats,
  trustBadges,
  whyDVV,
  heroVideo,
} from "../mock";
import Preloader from "@/components/Preloader";
import VideoPreloader from "@/components/Preloader";
import StatCounter from "@/components/StatCounter";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Home = ({ onBookingClick }) => {
  const [showPreloader, setShowPreloader] = useState(true);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
const [isModalOpen, setIsModalOpen] = useState(false);

  // Preloader timeout fallback
  useEffect(() => {
  const timer = setTimeout(() => {
    setShowPreloader(false);
  }, 5000);

  return () => clearTimeout(timer);
}, []);
const iconMap = {
  "shield-check": ShieldCheck,
  "map-pin": MapPin,
  "trending-up": TrendingUp
};
const navigate = useNavigate();


  // Testimonial carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  // Animated counter hook
  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const elementRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            let start = 0;
            const increment = end / (duration / 16);
            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(Math.floor(start));
              }
            }, 16);
            return () => clearInterval(timer);
          }
        },
        { threshold: 0.3 }
      );

      if (elementRef.current) {
        observer.observe(elementRef.current);
      }

      return () => observer.disconnect();
    }, [end, duration, hasAnimated]);

    return [count, elementRef];
  };
  const heroImages = [
  "beach.png",
  "residential.png",
  "farmland.png"
];
const [currentIndex, setCurrentIndex] = useState(0);
const slides = ["COLLIDE", "beach.png", "residential.png", "farmland.png"];
const [slideIndex, setSlideIndex] = useState(0);
// Auto-slide images every 5 seconds
useEffect(() => {
  const interval = setInterval(() => {
    setSlideIndex((prev) => (prev + 1) % slides.length);
  }, 4000);
  return () => clearInterval(interval);
}, []);


  return (
    <>
      <VideoPreloader
  isVisible={showPreloader}
  onFinish={() => setShowPreloader(false)}
/>
      <div
  className={`min-h-screen transition-opacity duration-700 ${
    showPreloader ? "opacity-0 pointer-events-none" : "opacity-100"
  }`}
      >
        {/* Hero Section - Only Video */}
<div id="hero-section" className="relative w-full h-screen overflow-hidden">

  <video
  autoPlay
  muted
  loop
  playsInline
  preload="metadata"
  className="absolute top-0 left-0 w-full h-full object-cover object-center"
>
  <source src="/Bgvid.mp4" type="video/mp4" />
</video>

  {/* Remove overlay or keep minimal */}
  {/* <div className="absolute inset-0 bg-black/5"></div> */}

</div>
        {/* Featured Projects Section */}
        <section id="projects" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">Featured Projects</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Explore our carefully curated residential and commercial properties across Visakhapatnam
              </p>
            </motion.div>

            {/* Residential Projects */}
            <motion.div 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-brand-dark mb-8 text-center">Residential Plot Projects</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {projects
                  .filter(p => p.category === 'residential')
                  .slice(0, 2)
                  .map((project, index) => (
                    <motion.div 
                      key={project.id}
                      className="scroll-reveal"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <Link to={`/projects/${project.id}`}>
                        <Card className="overflow-hidden h-full border-2 border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={project.images[0]}
                              alt={project.name}
                              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                            />
                            <div className="absolute top-3 right-3 bg-brand-gold text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {project.status}
                            </div>
                          </div>
                          <CardContent className="p-5">
                            <h3 className="text-xl font-bold text-brand-dark mb-2">{project.name}</h3>
                            <p className="text-sm text-gray-600 mb-3">{project.tagline}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                              <MapPin size={16} className="text-brand-gold" />
                              <span className="line-clamp-1">{project.location}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {project.approvals.slice(0, 2).map((approval, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded"
                                >
                                  {approval}
                                </span>
                              ))}
                            </div>
                            <p className="text-brand-gold font-bold">{project.priceRange}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
              </div>
            </motion.div>

            {/* Farmland Projects */}
            <motion.div 
              className=""
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-brand-dark mb-8 text-center">Farmland Plot Projects</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {projects
                  .filter(p => p.category === 'farmland')
                  .slice(0, 2)
                  .map((project, index) => (
                    <motion.div 
                      key={project.id}
                      className="scroll-reveal"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <Link to={`/projects/${project.id}`}>
                        <Card className="overflow-hidden h-full border-2 border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={project.images[0]}
                              alt={project.name}
                              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                            />
                            <div className="absolute top-3 right-3 bg-brand-gold text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {project.status}
                            </div>
                          </div>
                          <CardContent className="p-5">
                            <h3 className="text-xl font-bold text-brand-dark mb-2">{project.name}</h3>
                            <p className="text-sm text-gray-600 mb-3">{project.tagline}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                              <MapPin size={16} className="text-brand-gold" />
                              <span className="line-clamp-1">{project.location}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {project.approvals.slice(0, 2).map((approval, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded"
                                >
                                  {approval}
                                </span>
                              ))}
                            </div>
                            <p className="text-brand-gold font-bold">{project.priceRange}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
              </div>
            </motion.div>

            <motion.div 
              className="text-center mt-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/projects">
                <Button size="lg" className="btn-primary px-8 py-3 rounded-lg">
                  View All Projects
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Why DVV Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-gray-100">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">
                Why Choose DVV Projects?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Your trusted partner for property investment in Visakhapatnam
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
  {whyDVV.map((item, index) => {
    const Icon = iconMap[item.icon]; // <-- map string to component
    return (
      <motion.div 
        key={index}
        className="group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
      >
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-brand-gold/20 to-brand-gold/40 
                      flex items-center justify-center mb-6 
                      group-hover:scale-110 transition-transform duration-500">
          {Icon && <Icon size={36} className="text-brand-gold" />}  {/* <- fixed */}
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-brand-dark mb-3 text-center">
          {item.title}
        </h3>
        <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>
      </motion.div>
    );
  })}
</div>

          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-brand-dark text-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                const [count, elementRef] = useCounter(stat.value);
                return (
                  <motion.div 
                    key={index}
                    ref={elementRef}
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="text-4xl md:text-5xl font-bold text-brand-gold mb-2">
                      {count}{stat.suffix}
                    </div>
                    <div className="text-gray-300">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Strategic Locations */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">Strategic Locations</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our projects are located in high-growth corridors of Visakhapatnam
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: "Visakhapatnam Airport", distance: "35 KM", icon: Plane },
                { name: "Atchyutapuram SEZ", distance: "20 KM", icon: Building },
                { name: "Coastal Corridor Highway", distance: "1 KM", icon: Route },
                { name: "Beach Road", distance: "5 KM", icon: Sun },
                { name: "Lambasingi Hills", distance: "60 KM", icon: Trees },
                { name: "Industrial Hub", distance: "30 KM", icon: Factory },
              ].map((location, index) => (
                <motion.div 
                  key={index}
                  className="group bg-gradient-to-br from-brand-dark to-brand-dark/80 text-white p-6 rounded-xl text-center border border-white/10 hover:-translate-y-1 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.25)]"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                    <location.icon size={30} className="text-brand-gold" />
                  </div>
                  <h4 className="font-bold mb-1">{location.name}</h4>
                  <p className="text-sm text-brand-gold relative inline-block">
                    {location.distance} away
                    <span className="absolute left-0 -bottom-1 w-full h-[2px] bg-brand-gold scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20" style={{ backgroundColor: 'var(--bg-3)' }}>
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">What Our Clients Say</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Real experiences from real people who invested with DVV Projects
              </p>
            </motion.div>

            <motion.div 
              className="relative max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonials[currentTestimonial].avatar}
                    alt={testimonials[currentTestimonial].name}
                    className="w-16 h-16 rounded-full mr-4 object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-brand-dark text-lg">
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className="text-gray-600 text-sm">{testimonials[currentTestimonial].role}</p>
                    <p className="text-brand-gold text-xs">{testimonials[currentTestimonial].location}</p>
                  </div>
                  <div className="ml-auto flex gap-1">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </p>
              </div>

              {/* Navigation Buttons */}
              <button
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg"
                aria-label="Next testimonial"
              >
                <ChevronRight size={24} />
              </button>

              {/* Indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentTestimonial
                        ? 'bg-brand-gold w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-br from-brand-dark to-brand-dark/90 text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.h2 
              className="text-3xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Ready to Invest in Your Dream Property?
            </motion.h2>
            <motion.p 
              className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-brand-gold"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Book a site visit today and explore VMRDA approved properties ready for registration
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button
  onClick={() => onBookingClick()}
  size="lg"
  className="btn-primary px-10 py-6 text-lg font-semibold rounded-lg hover:bg-brand-gold/90 transition-colors"
>
  Book Your Site Visit Now
</Button>

            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;